/* eslint indent: 0 */

import { triggerFirebaseEvent } from 'zoomforth_tools/firebase_events.js';

import get from 'lodash/get';
import pick from 'lodash/pick';
import isNil from 'lodash/isNil';
import includes from 'lodash/includes';
import bind from 'lodash/bind';
import debounce from 'lodash/debounce';
import filter from 'lodash/filter';

import {
  PAGE_STATUS_TYPE_ACTIVE,
  PAGE_STATUS_TYPE_ARCHIVED,
  PAGE_STATUS_TYPE_REDIRECTED,
  PAGE_STATUS_TYPE_DEACTIVATED,
  PAGE_STATUS_TYPE_NEVER_PUBLISHED,
  PAGE_STATUS_TYPE_SCHEDULED_FOR_REDIRECT,
  PAGE_STATUS_TYPE_SCHEDULED_FOR_DEACTIVATION,
} from 'zoomforth_primary_dashboard/const/pages.js';

import Backbone from 'backbone';
import $ from 'jquery';
import { get as allUsersStore } from '../appglobals/all-users.js';

import { displayBestErrorAlertForXhrFail } from 'zoomforth_tools/modern_helpers.js';

import { PAGE_EMAIL_TOKEN_TYPE_ONE_TIME } from 'zoomforth_shared_management/const/visitor_access_settings.js';

const Page = Backbone.Model.extend({

    urlRoot: '/api/pages',
    
    initialize: function(attributes, options) {
          
      this.contentCollection = null;
      if (options && options.contentCollection) {
        this.contentCollection = options.contentCollection;
      }
          
      this.pageTrackingMetadata = {
        page_id: this.get('id')
      };

      this.on('error', function(model, xhr) {
        isSyncing = false;
        displayBestErrorAlertForXhrFail(xhr);
        var changed = this.changedAttributes();

        if (!changed) return;

        var keys = Object.keys(changed);
        var prev = pick(this.previousAttributes(), keys);

        this.set(prev, {silent: true});
      });
      
      // we need this to prevent fetches from bouncing back and forth between browsers,
      // because fetch will often trigger a change event, and a change event will trigger a firebase update.
      // so we provide a method "fetchFromFirebaseUpdate" which both debounces, and prevents
      // a firebase event from being triggered by the updated changed models.
      // silencing the change event would be bad, because things (like views) need
      // to know that the change has happened, so we have to do it this crazy way
      this.skipFirebaseTriggerOnNextSync = false;
      
      var isSyncing = false;
      
      this.on('change', bind(function() {
        if (isSyncing) {
          return;
        }
        isSyncing = true;
        this.once('sync', function(){
          if (!this.skipFirebaseTriggerOnNextSync){
            triggerFirebaseEvent('lens-status-state-change', {
              lensId: this.get('id')
            });
          }
          isSyncing = false;
          this.skipFirebaseTriggerOnNextSync = false;
        }, this);
      }, this));
      
      this.fetchFromFirebaseUpdate = (callback)=>{
        debounce(function() {
          this.fetch({
            success: () => {
              this.skipFirebaseTriggerOnNextSync = true;
            }
          }).done(()=>{
            if (callback) {
              callback();
            }
          });
        }, 1500);
      };
    },
    
    publishThisVersion: function() {
      return this.save({
        published_version: this.get('published_version') + 1,
        isDraft: false

      }, { patch:true });
    },
        
    isEditable: function() {
      return !this.isLegacySpecies();
    },
    
    getEditorUrl: function() {
      if (this.isTemplate()) {
        return '/template-editor/' + this.get('urlId');
      }
      return '/site-editor/' + this.get('urlId');
    },

    getEditorTargetName: function() {
      return `editor-tab-${this.get('urlId')}`;
    },


    /**
      Return true if this page has never been published
    */
    isNeverPublished: function() {
      return !this.get('is_published');
    },

    isPendingApproval: function() {
      return !!this.get('approval_pending');
    },
    
    isTemplate : function(){
      return !!this.get('is_template');
    },

    getIsZoomforthSuperTemplate : function(){
      return this.isTemplate() && this.get('is_super');
    },
    
    getCopiedFromPageId: function() {
      return this.get('copied_from');
    },

    hasScreenshot: function() {
      return !!(this.get('screenshot_fullpage') || this.get('screenshot_standard'));
    },

    hasUnpublishedChanges: function() {
      return !!this.get('isDraft');
    },
    
    hasDefaultLink: function(){
      return !this.get('allow_new_links') && this.get('canonical_url');
    },
    
    revertDraft : function(){
      // this function is not restful, and we do not update the model by default
      var me = this;
      return $.ajax({
        url: '/v2/lenses/'+this.get('id')+'/revert',
        contentType: 'application/json; charset=utf-8',
        data: JSON.stringify(me),
        dataType: 'json',
        type: 'post',
        error: displayBestErrorAlertForXhrFail
      });
    }, 
    
    isTeamPage: function() {
      return this.get('is_team_lens');
    },
    
    isAccessibleBy: function(user) {
      return (
        user.canViewAllPages() ||
        this.isTeamPage() ||
        includes(this.get('assignees'), user.id)
      );
    },
    
    isCreatedBy: function(userId){
      return this.get('created_by_user') === userId;
    },
    
    /**
      Return true if this page has ever been published
    */
    isPublished: function(){
      return !!this.get('is_published');
    },

    isRedirected: function() {
      return this.get('is_redirected');
    },

    isRedirectScheduled: function() {
      return this.get('is_redirect_scheduled');
    },

    getRedirectType: function() {
      // we want these all to be strings because they need to live in radio buttons
      if (this.get('redirect_lens_id')) {
        return 'to_page';
      } else if (this.get('page_redirect_url')) {
        return 'to_url';
      }
      return 'no_redirect';
    },

    isArchived: function() {
      return !!this.get('is_archived');
    },

    isDeactivated: function() {
      return !!this.get('is_deactivated');
    },

    isIndexable: function() {
      var isIndexable = this.get('is_indexable');
      if (isNil(isIndexable)) {
        isIndexable = true;
      }
      return this.isPublic() && isIndexable;
    },
    
    isPublic: function() {
      return this.getVisitorAccessSetting() == 'public';
    },
    
    usesSAMLPrivacy: function() {
      return this.getVisitorAccessSetting() == 'SAML';
    },

    usesMfaPrivacy: function() {
      return this.getVisitorAccessSetting() == 'mfa';
    },
    
    usesPasswordPrivacy: function() {
      return this.getVisitorAccessSetting() == 'password';
    },
    
    usesEmailPrivacy: function() {
      return this.getVisitorAccessSetting() == 'email';
    },
    
    getVisitorAccessSetting: function() {
      return this.get('privacy_setting');
    },
    
    privacySettingKnowsIdentity: function() {
      return this.usesSAMLPrivacy() || this.usesEmailPrivacy() || this.usesMfaPrivacy();
    },
    
    getDeactivationSchedule: function(){
      return this.get('deactivation_scheduled_at');
    },

    /**
     * getPageStatusTypeString attempts to return an array of status strings
     * that apply to the site.
     *
     * In a simplified sense we have a primary status and secondary status. The
     * only time a secondary status should be appended is if the site is active
     * with additional pending status parameters.
     */
    getApplicableStatusStrings: function() {
      const statusStrings = [];

      // determine primary status string
      if (this.isArchived()) {
        statusStrings.push(PAGE_STATUS_TYPE_ARCHIVED);

      } else if (this.isRedirected()) {
        statusStrings.push(PAGE_STATUS_TYPE_REDIRECTED);

      } else if (this.isDeactivated()) {
        statusStrings.push(PAGE_STATUS_TYPE_DEACTIVATED);

      } else if (this.isNeverPublished()) {
        statusStrings.push(PAGE_STATUS_TYPE_NEVER_PUBLISHED);

      } else {
        statusStrings.push(PAGE_STATUS_TYPE_ACTIVE);

        // determine secondary status string
        if (this.isRedirectScheduled()) {
          statusStrings.push(PAGE_STATUS_TYPE_SCHEDULED_FOR_REDIRECT);
        }

        if (this.getDeactivationSchedule()) {
          statusStrings.push(PAGE_STATUS_TYPE_SCHEDULED_FOR_DEACTIVATION);
        }

      }

      return statusStrings;
    },

    getDoesPageMatchStatusType: function(statusType){
      return includes(this.getApplicableStatusStrings(), statusType);
    },

    getEmailAuthTokenType: function() {
      if (this.usesEmailPrivacy() || this.usesMfaPrivacy()) {
        const currentPermissions = this.get('permissions') || {};
        return get(currentPermissions, 'token_type', PAGE_EMAIL_TOKEN_TYPE_ONE_TIME);
      }

      return null;
    },

    getVisitorAccessEmailAuthWhitelist: function() {
      const defaultPermissions = { domains: [], emails: [] };
      const currentPermissions = this.get('permissions') || {};

      return { ...defaultPermissions, ...currentPermissions };
    },

    getBrandedAuthEmailTemplateId: function() {
      return this.get('branded_auth_email_template_id');
    },

    getSamlAccessConfig: function() {
      const defaultConfig = {
        use_whitelist: false,
        use_groups: false,
        emails: [],
        groups: []
      };

      const currentConfig = this.get('saml_access_config') || {};

      return { ...defaultConfig, ...currentConfig };
    },

    getVisitorAccessSAMLAuthGroups: function() {
      const defaultPermissions = { use_groups: false, groups: [] };
      const currentPermissions = this.get('saml_access_config') || {};

      return { ...defaultPermissions, ...currentPermissions };
    },
    
    getAssignees: function() {
      var assigneeIds;
      var assignees = [];
      var usersThatCanBeAssignees = allUsersStore().getAssignableUsers();
      
      if (this.isTeamPage()) {
        assignees = usersThatCanBeAssignees;
      } else {
        assigneeIds = this.get('assignees');
        assignees = filter(usersThatCanBeAssignees, function(user){
          return includes(assigneeIds, user.get('id'));
        });
      }
      return assignees;
    },

    /**
      
      Returns string representing if this page is of type
      `grid`, `sections` or `galleria`

    */
    getSpecies() {
      return this.get('species');
    },

    savePermissions(newPermissions, options) {
      const currentPermissions = this.get('permissions') || {};
      return this.save(
        {
          permissions: {
            ...currentPermissions,
            ...newPermissions
          }
        },
        options
      );
    },

    isLegacySpecies() {
      return includes(['galleria', 'sections'], this.getSpecies());
    },
  
    destroy() {
      const isNew = this.isNew();

      return Page.__super__.destroy.apply(this, arguments)
  
        .done(()=> {
          if (!isNew) {
            triggerFirebaseEvent('pages-collection-change', {});
          }
        });      
    },

    save() {
  
      const isNew = this.isNew();

      return Page.__super__.save.apply(this, arguments)
  
        .done(()=> {
          if (isNew) {
            triggerFirebaseEvent('pages-collection-change', {});
          }
        });
    }

  });

export default Page;
